import React from 'react';
import { Modal, Form, Input, Select, Upload, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Option } = Select;

export interface CreateTicketModalProps {
  visible: boolean;
  form: any;
  fileList: UploadFile[];
  onCancel: () => void;
  onSubmit: () => void;
  onFileChange: (info: UploadChangeParam) => void;
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  visible,
  form,
  fileList,
  onCancel,
  onSubmit,
  onFileChange,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('CreateNewTicket')}
      visible={visible}
      onOk={onSubmit}
      onCancel={() => {
        onCancel();
        form.resetFields();
      }}
      okText={t('Create')}
      cancelText={t('Cancel')}
      width={700}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="subject"
          label={t('Subject')}
          rules={[{ required: true, message: t("Please enter a subject for your ticket") }]}
        >
          <Input placeholder={t("Describe the problem briefly")} />
        </Form.Item>

        <Form.Item
          name="category"
          label={t('Category')}
          rules={[{ required: true, message: t("PleaseSelectCategory") }]}
          initialValue="technical"
        >
          <Select>
            <Option value="payment">{t('supportCategories.payment')}</Option>
            <Option value="project">{t('supportCategories.project')}</Option>
            <Option value="employee">{t('supportCategories.employee')}</Option>
            <Option value="tests">{t('supportCategories.tests')}</Option>
            <Option value="time">{t('supportCategories.time')}</Option>
            <Option value="calendar">{t('supportCategories.calendar')}</Option>
            <Option value="technical">{t('supportCategories.technical')}</Option>
            <Option value="advice">{t('supportCategories.advice')}</Option>
            <Option value="other">{t('supportCategories.other')}</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label={t('Description')}
          rules={[{ required: true, message: t("Please provide a detailed description") }]}
        >
          <TextArea
            rows={6}
            placeholder={t("Describe the problem briefly")}
          />
        </Form.Item>

        <Form.Item
          name="attachments"
          label={t('Attachments')}
          valuePropName="fileList"
          getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
        >
          <Upload
            beforeUpload={(file) => {
              const isLt30M = file.size / 1024 / 1024 <= 10;
              if (!isLt30M) {
                message.error(`${file.name}: ${t("FileSizeLimit", { size: 10 })}`);
                return Upload.LIST_IGNORE;
              }
              return false;
            }}
            multiple
            listType="picture"
            fileList={fileList}
            onChange={(info) => {
              if (info.fileList.length > 3) {
                message.warning(t("MaxFilesLimit", { count: 3 }) || "Максимум 3 файли");
                return;
              }

              onFileChange(info);

              if (info.file.status === "done") {
                message.success(`${info.file.name} ${t("UploadSuccess")}`);
              } else if (info.file.status === "error") {
                message.error(`${info.file.name} ${t("UploadFailed")}`);
              }
            }}

          >
            <Button icon={<UploadOutlined />}>{t('AddFiles')}</Button>
          </Upload>
          <div style={{ marginTop: 8, color: "#999" }}>
            {t("UploadLimitHint")}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;
